import { useTheme } from '@repo/ui/components/theme-provider'
import { surfaceRadii } from '@repo/ui/lib/design-tokens'
import { cn } from '@repo/ui/lib/utils'
import type { Feature, FeatureCollection, GeoJsonProperties, Geometry, Point } from 'geojson'
import maplibregl, { type GeoJSONSource, type MapLayerMouseEvent } from 'maplibre-gl'
import { useEffect, useMemo, useRef } from 'react'
import {
  createMap,
  createMapStyle,
  escapeMapHtml,
  fitMapToPoints,
  invalidateMapSize,
  type LngLat,
  latLngToLngLat,
  syncMapWhenStyleIsReady,
} from '@/lib/maplibre'

interface PlaceMarker {
  id: string
  label: string
  addressText: string | null
  lat: string | null
  lng: string | null
}

type PlaceProperties = {
  label: string
  addressText: string | null
}

const emptyPlaces: FeatureCollection<Point, PlaceProperties> = {
  type: 'FeatureCollection',
  features: [],
}

export function PlacesMap({
  places,
  className,
  defaultCenter,
  viewportSignal,
}: {
  places: PlaceMarker[]
  className?: string
  defaultCenter?: [number, number]
  viewportSignal?: string | number | boolean
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const popupRef = useRef<maplibregl.Popup | null>(null)
  const { resolvedTheme } = useTheme()

  const fallbackCenter = useMemo(
    () => defaultCenter ?? ([39.8283, -98.5795] as [number, number]),
    [defaultCenter]
  )
  const points = useMemo(
    () =>
      places
        .filter((place) => place.lat && place.lng)
        .map((place) => ({
          ...place,
          point: [Number(place.lat), Number(place.lng)] as [number, number],
        }))
        .filter((place) => Number.isFinite(place.point[0]) && Number.isFinite(place.point[1])),
    [places]
  )
  const placesData = useMemo<FeatureCollection<Point, PlaceProperties>>(
    () => ({
      type: 'FeatureCollection',
      features: points.map((place) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: latLngToLngLat(place.point),
        },
        properties: {
          label: place.label,
          addressText: place.addressText,
        },
      })),
    }),
    [points]
  )
  const fitPoints = useMemo<LngLat[]>(
    () => points.map((place) => latLngToLngLat(place.point)),
    [points]
  )

  useEffect(() => {
    const container = containerRef.current
    if (!container || mapRef.current) return

    const map = createMap({
      container,
      center: fallbackCenter,
      zoom: 10,
      isDark: resolvedTheme === 'dark',
    })
    mapRef.current = map

    map.on('load', () => {
      addPlacesSourcesAndLayers(map, resolvedTheme === 'dark')
      setSourceData(map, 'places', placesData)
      fitMapToPoints({ map, points: fitPoints, fallbackCenter, fallbackZoom: 10, maxZoom: 13 })
    })

    return () => {
      popupRef.current?.remove()
      popupRef.current = null
      map.remove()
      mapRef.current = null
    }
  }, [fallbackCenter, fitPoints, placesData, resolvedTheme])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    map.setStyle(createMapStyle(resolvedTheme === 'dark'))
  }, [resolvedTheme])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    return syncMapWhenStyleIsReady(map, () => {
      addPlacesSourcesAndLayers(map, resolvedTheme === 'dark')
      setSourceData(map, 'places', placesData)
    })
  }, [placesData, resolvedTheme])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const handleClick = (event: MapLayerMouseEvent) => {
      const feature = event.features?.[0] as Feature<Point, PlaceProperties> | undefined
      if (!feature) return
      popupRef.current?.remove()
      popupRef.current = new maplibregl.Popup({ closeButton: false, maxWidth: '220px' })
        .setLngLat(event.lngLat)
        .setHTML(renderPlacePopup(feature.properties))
        .addTo(map)
    }
    const handleEnter = () => {
      map.getCanvas().style.cursor = 'pointer'
    }
    const handleLeave = () => {
      map.getCanvas().style.cursor = ''
    }

    map.on('click', 'place-point', handleClick)
    map.on('mouseenter', 'place-point', handleEnter)
    map.on('mouseleave', 'place-point', handleLeave)
    return () => {
      map.off('click', 'place-point', handleClick)
      map.off('mouseenter', 'place-point', handleEnter)
      map.off('mouseleave', 'place-point', handleLeave)
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    const container = containerRef.current
    if (!map || !container) return

    void viewportSignal
    const resize = () => invalidateMapSize(map)
    const frame = window.requestAnimationFrame(() => {
      resize()
      fitMapToPoints({ map, points: fitPoints, fallbackCenter, fallbackZoom: 10, maxZoom: 13 })
    })
    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(container)
    window.addEventListener('resize', resize)

    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', resize)
      resizeObserver.disconnect()
    }
  }, [fallbackCenter, fitPoints, viewportSignal])

  return (
    <div
      className={cn(
        'relative z-0 h-full overflow-hidden border border-border/70',
        surfaceRadii.shell,
        className
      )}
    >
      <div ref={containerRef} className="h-full min-h-[320px] w-full" />
    </div>
  )
}

function addPlacesSourcesAndLayers(map: maplibregl.Map, isDark: boolean) {
  if (!map.isStyleLoaded()) return

  const pointColor = isDark ? '#b87b63' : '#9d654f'
  const strokeColor = isDark ? '#17130f' : '#f4f1eb'

  if (!map.getSource('places')) {
    map.addSource('places', { type: 'geojson', data: emptyPlaces })
  }
  if (!map.getLayer('place-point')) {
    map.addLayer({
      id: 'place-point',
      type: 'circle',
      source: 'places',
      paint: {
        'circle-radius': 6,
        'circle-color': '#9d654f',
        'circle-stroke-color': '#f4f1eb',
        'circle-stroke-width': 2,
      },
    })
  }
  map.setPaintProperty('place-point', 'circle-color', pointColor)
  map.setPaintProperty('place-point', 'circle-stroke-color', strokeColor)
  map.setPaintProperty('place-point', 'circle-stroke-width', 2)
}

function setSourceData(
  map: maplibregl.Map,
  sourceId: string,
  data: FeatureCollection<Geometry, GeoJsonProperties>
) {
  const source = map.getSource(sourceId) as GeoJSONSource | undefined
  source?.setData(data)
}

function renderPlacePopup(properties: PlaceProperties) {
  return `
    <div style="min-width: 140px; color: var(--card-foreground);">
      <div style="font-weight: 600; font-size: 14px;">${escapeMapHtml(properties.label)}</div>
      ${
        properties.addressText
          ? `<div style="margin-top: 4px; color: var(--muted-foreground); font-size: 13px; line-height: 1.4;">${escapeMapHtml(properties.addressText)}</div>`
          : ''
      }
    </div>
  `
}
